/* ==========================================================================
   YBTCO Script Loader  —  Phase 3 (May 2026)
   --------------------------------------------------------------------------
   PURPOSE
     A single, controlled place to load third-party JavaScript AFTER the page
     is interactive. Keeps render-blocking work off the critical path and
     prevents pixels / widgets from inflating LCP and TBT.

   WHO TOUCHES THIS FILE
     A non-technical operator can safely add a new script by copying one of
     the entries in THIRD_PARTY_SCRIPTS below and changing the values. No
     other code in this file needs to change.

   WHEN SCRIPTS RUN
     - On any browser that supports it, scripts wait for `requestIdleCallback`
       (the browser tells us when the main thread is free).
     - Older Safari versions fall back to a small `setTimeout` delay so the
       initial render still completes first.
     - Scripts are added ONCE per page-load and de-duplicated by URL.

   HOW TO ADD A SCRIPT
     1. Add an object to the THIRD_PARTY_SCRIPTS array below.
     2. Required fields:
          name      — short label, used only for the console log.
          src       — the full https:// URL of the script.
          when      — a function returning true if the script should load
                      on the current page. Use the helpers (isProduct, etc.)
                      or write your own check against window.location.
     3. Optional fields:
          async     — defaults to true. Set false only if the vendor demands
                      synchronous execution (rare).
          attrs     — object of extra DOM attributes, e.g. {"data-cfasync":"false"}.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- Page-type helpers --------------------------------------- */
  /* These read the body class set by theme.liquid:
     <html class="... template-{{ template.name }} ..."> */
  var docClass = (document.documentElement.className || '');

  function isTemplate(name) {
    return docClass.indexOf('template-' + name) !== -1;
  }
  var isProduct    = function () { return isTemplate('product'); };
  var isCollection = function () { return isTemplate('collection'); };
  var isArticle    = function () { return isTemplate('article'); };
  var isCart       = function () { return isTemplate('cart'); };
  var isHome       = function () { return isTemplate('index'); };
  var isAnyPage    = function () { return true; };

  /* ---------- SCRIPT REGISTRY  (edit this list) ----------------------- */
  /* To add a new third-party script, copy an entry and edit it.
     To remove one, delete the entry. That's it. */
  var THIRD_PARTY_SCRIPTS = [

    /* Judge.me loads via its own app embed (judgeme_core, enabled in theme settings),
       so it needs NO manual entry here. Removed 2026-05-31 to prevent double-loading
       the Judge.me script. Add other third-party scripts below by copying an example. */

    /* ---- Example entries for future use (commented out) -----------------
    ,{
      name: 'Meta (Facebook) Pixel loader',
      src:  'https://connect.facebook.net/en_US/fbevents.js',
      when: isAnyPage
    },
    {
      name: 'TikTok Pixel',
      src:  'https://analytics.tiktok.com/i18n/pixel/events.js',
      when: isAnyPage
    },
    {
      name: 'Klaviyo on-site tracking',
      src:  'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=XXXXXX',
      when: isAnyPage
    }
    --------------------------------------------------------------------- */
  ];

  /* ---------- Loader internals (no edits needed below this line) ------ */
  var loaded = {};

  function injectScript(entry) {
    if (loaded[entry.src]) return;
    loaded[entry.src] = true;

    var s = document.createElement('script');
    s.src = entry.src;
    s.async = (entry.async === false) ? false : true;
    if (entry.attrs) {
      for (var key in entry.attrs) {
        if (Object.prototype.hasOwnProperty.call(entry.attrs, key)) {
          s.setAttribute(key, entry.attrs[key]);
        }
      }
    }
    s.onerror = function () {
      // Vendor outage shouldn't surface as a hard error to shoppers.
      // We log so it's visible in DevTools but don't throw.
      // eslint-disable-next-line no-console
      console.warn('[script-loader] Failed to load:', entry.name, entry.src);
    };
    document.head.appendChild(s);
  }

  function runQueue() {
    for (var i = 0; i < THIRD_PARTY_SCRIPTS.length; i++) {
      var entry = THIRD_PARTY_SCRIPTS[i];
      try {
        if (entry && entry.src && typeof entry.when === 'function' && entry.when()) {
          injectScript(entry);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[script-loader] Skipped entry:', entry && entry.name, err);
      }
    }
  }

  function scheduleQueue() {
    // Prefer requestIdleCallback so we run only when the main thread is free.
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(runQueue, { timeout: 4000 });
    } else if ('IntersectionObserver' in window) {
      // Fallback 1: wait for the first paint by observing <body>.
      var io = new IntersectionObserver(function (entries, obs) {
        if (entries.some(function (e) { return e.isIntersecting; })) {
          obs.disconnect();
          setTimeout(runQueue, 0);
        }
      });
      if (document.body) {
        io.observe(document.body);
      } else {
        setTimeout(runQueue, 1500);
      }
    } else {
      // Fallback 2: just delay a touch so initial render wins the frame.
      setTimeout(runQueue, 1500);
    }
  }

  // Wait for DOM so document.body / template class are reliably available.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleQueue, { once: true });
  } else {
    scheduleQueue();
  }
})();
